import "core-js/modules/es.array.reverse.js";
var _class, _descriptor;
import "core-js/modules/es.symbol.description.js";
import "core-js/modules/web.dom-collections.iterator.js";
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { Document } from "flexsearch";
import { action, makeObservable, observable, runInAction } from "mobx";
import { isJsonObject, isJsonString, isJsonStringArray } from "../../Core/Json";
import loadBlob, { isZip, parseZipJsonBlob } from "../../Core/loadBlob";
import loadJson from "../../Core/loadJson";
import CatalogIndexReference from "../Catalog/CatalogReferences/CatalogIndexReference";
import CommonStrata from "../Definition/CommonStrata";
import updateModelFromJson from "../Definition/updateModelFromJson";
import SearchResult from "./SearchResult";
let CatalogIndex = (_class = class CatalogIndex {
  constructor(terria, url) {
    this.terria = terria;
    this.url = url;
    /** Map from share key -> id */
    _defineProperty(this, "shareKeysMap", observable.map());
    _defineProperty(this, "_models", void 0);
    _defineProperty(this, "_searchIndex", void 0);
    // Flex-search document index
    _initializerDefineProperty(this, "_loadPromise", _descriptor, this);
    makeObservable(this);
  }
  get models() {
    return this._models;
  }
  get searchIndex() {
    return this._searchIndex;
  }
  get loadPromise() {
    return this._loadPromise;
  }
  getModelByIdOrShareKey(modelId) {
    var _this$models;
    if ((_this$models = this.models) !== null && _this$models !== void 0 && _this$models.has(modelId)) {
      return this.models.get(modelId);
    }
    const shareKeyId = this.shareKeysMap.get(modelId);
    if (shareKeyId) {
      var _this$models2;
      return (_this$models2 = this.models) === null || _this$models2 === void 0 ? void 0 : _this$models2.get(shareKeyId);
    }
  }
  load() {
    if (this._loadPromise) return this._loadPromise;
    runInAction(() => this._loadPromise = this.loadCatalogIndex());
    return this._loadPromise;
  }

  /** The catalog index is loaded automatically on startup.
   * It is loaded the first time loadInitSources is called (see Terria.forceLoadInitSources) */
  async loadCatalogIndex() {
    // Load catalog index
    try {
      const url = this.terria.corsProxy.getURLProxyIfNecessary(this.url);
      const index = isZip(url) ? await parseZipJsonBlob(await loadBlob(url)) : await loadJson(url);
      this._models = new Map();

      /**
       * https://github.com/nextapps-de/flexsearch
       * Create search index for fields "name" and "description"
       *  - tokenize property
       *    - "full" = index every possible combination
       *    - "strict" = index whole words
       *  - resolution property = score resolution
       *
       * Note: because we have set `worker: true`, we must use async calls
       */
      this._searchIndex = new Document({
        worker: true,
        document: {
          id: "id",
          index: [{
            field: "name",
            tokenize: "full",
            resolution: 9
          }, {
            field: "description",
            tokenize: "strict",
            resolution: 1
          }]
        }
      });
      const indexModels = Object.entries(index);
      const promises = [];
      for (let idx = 0; idx < indexModels.length; idx++) {
        const [id, model] = indexModels[idx];
        if (!isJsonObject(model, false)) return;
        const reference = new CatalogIndexReference(id, this.terria);
        updateModelFromJson(reference, CommonStrata.definition, model).logError("Error ocurred adding adding catalog model reference");
        if (isJsonStringArray(model.shareKeys)) {
          model.shareKeys.map(s => this.shareKeysMap.set(s, id));
        }
        // Add model to CatalogIndexReference map
        this._models.set(id, reference);

        // Add document to search index
        promises.push(this._searchIndex.addAsync(id, {
          id,
          name: isJsonString(model.name) ? model.name : "",
          description: isJsonString(model.description) ? model.description : ""
        }));
      }
      await Promise.all(promises);
    } catch (error) {
      this.terria.raiseErrorToUser(error, "Failed to load catalog index");
    }
  }
  async search(q) {
    const results = [];
    /** Example matches object
    ```json
    [
      {
        "field": "name",
        "result": [
          "some-id-1"
        ]
      },
      {
        "field": "description",
        "result": [
          "some-id-2"
        ]
      }
    ]
    ```
    */
    if (!this.searchIndex) return [];
    const matches = await this.searchIndex.searchAsync(q);
    const matchedIds = new Set();
    matches.forEach(fieldResult => {
      fieldResult.result.forEach(id => {
        var _this$models3;
        const indexReference = (_this$models3 = this.models) === null || _this$models3 === void 0 ? void 0 : _this$models3.get(id);
        if (indexReference && !matchedIds.has(id)) {
          matchedIds.add(id);
          results.push(runInAction(() => {
            var _indexReference$name;
            return new SearchResult({
              name: (_indexReference$name = indexReference.name) !== null && _indexReference$name !== void 0 ? _indexReference$name : indexReference.uniqueId,
              catalogItem: indexReference
            });
          }));
        }
      });
    });
    return results;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "_loadPromise", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "loadCatalogIndex", [action], Object.getOwnPropertyDescriptor(_class.prototype, "loadCatalogIndex"), _class.prototype), _class);
export { CatalogIndex as default };